import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Button, Icon, RobotoFontFamily, TextBadge, Typography } from '@packages/ui/shared';
import {
    SORT_DIRECTION,
    TableActionHeader,
    TableCell,
    TableCheckbox,
    TableHeader,
    TableRenderer,
    TableRow,
} from '@web/components/table';
import { Download, Eye, Favourite, FavouriteOpen, NoFoundError, Share } from '@web/vin-sessions/assets/Icons';
import colors from '@packages/core/styles/colors';
import { getLogger } from '@packages/core/logging';
import { HoverModal } from '../docs/hover-modal';
import { PublicationType } from './publication-type-icons';
import {
    DocumentVersion,
    PublicationStatusEnum,
    statusVariants,
    StisDocument,
    StisPublicationAction,
} from '@web/vin-sessions/types';
import { useAuthState } from '@packages/contexts/auth';
import { useGoogleAnalytics } from '@web/core/google-analytics/use-google-analytics';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics';
import { Link, PathMatch, useNavigate, useParams } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useQuery } from '@web/core/hooks';
import { docsService, httpClient } from '@web/services/singletons';
import { handleCopy } from '@web/vin-sessions/utils/vinSessionUtils';
import { openDocumentTab, resolveDocumentUrl } from '@web/vin-sessions/utils/open-document-tab';
import { usePublicationsDispatch } from './context/publications.hooks';
import { publicationsActions } from './context/publications.state';
import { Account } from '@packages/models/api';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { analyticsService } from '@web/services/singletons';
import { getNextVersion, parseVersion } from './version.utils';
import { getSaveErrorMessage } from './save-error.utils';

const logger = getLogger('vin-sessions');

type StisTableProps = {
    rowId?: 1;
    isLoading: boolean;
    publications: StisDocument[];
    sortOrder?: string | null;
    sortDirection?: SORT_DIRECTION | null;
    onSort(sortBy: string, sortDirection: SORT_DIRECTION): void;
    handleFavoriteClick: (id: string, isFavorite: boolean) => void;
    handleRemoveRecentClick?: (ids: string[]) => void;
    showLastViewed?: boolean;
    handlePreviewButtonClick: (
        id: string,
        title: string,
        pageNumber: number,
        documentType?: string,
        fileName?: string,
        createdDate?: string,
        lastUpdated?: string
    ) => void;
    getPublicationVersions: (id: string) => void;
    isManageAdminRoute?: PathMatch<'*'> | null;
    onPopupBlocked?: () => void;
    totalElements?: number;
    emptyStateDescription: string;
};

interface NavigateUrl {
    publicationId: string;
    status: PublicationStatusEnum;
    version: string;
    isPreview?: boolean;
}

interface RowData extends StisDocument {
    isExpanded: boolean;
    associatedVersions: DocumentVersion[];
    index: number;
    rowId: string;
}

const useStyles = createUseStyles({
    iconsContainer: {
        height: 56,
        display: 'flex',
        alignItems: 'center',
        gap: 5,
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        maxWidth: '80%',
    },
    popOver: {
        minWidth: 400,
        color: '#333',
        border: '1px solid #ddd',
        borderRadius: 8,
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        padding: 15,
    },
    description: {
        border: '1px solid #e0e0e0',
        padding: 10,
        borderRadius: 5,
        backgroundColor: '#f7f7f7',
        fontFamily: RobotoFontFamily.Regular,
        fontWeight: '400',
        fontSize: 15,
        maxHeight: 224,
        overflowY: 'auto !important',
        '&::-webkit-scrollbar': {
            width: 10,
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: '#f7f7f7',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.grayThree,
            borderRadius: 10,
            border: '2px solid #f7f7f7',
        },
    },
    layers: {
        display: 'flex',
        alignItems: 'center',
        fontWeight: '400',
        fontSize: 15,
        gap: 5,
    },
    icons: {
        height: 40,
        width: 40,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 4,
        '&:hover': {
            backgroundColor: '#dee6ef',
        },
    },
    documentType: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    outerTableClass: {
        height: '100%',
        maxHeight: 'calc(100vh - 300px) !important',
        minHeight: 'calc(100vh - 350px)',
    },
    customTooltip: {
        fontSize: 16,
        padding: '8px 12px',
        textAlign: 'center',
        marginTop: '-4px !important',
    },
});

const useStyleWrapper = createUseStyles((show: string) => {
    const borderStyle = show ? `2px solid ${colors.blueOne}` : 0;
    return {
        subRows: {
            display: show ? '' : 'none',
            backgroundColor: colors.grayTwo,
            borderLeft: borderStyle,
            borderRight: borderStyle,
            transition: '3s display',
        },
        parentRow: {
            borderTop: borderStyle,
            borderLeft: borderStyle,
            borderRight: borderStyle,
        },
        bottomBorder: {
            borderBottom: `2px solid ${colors.blueOne} !important`,
        },
        displayHide: {
            display: show ? '' : 'none',
            transition: '3s display',
        },
    };
});

const formatDate = (isoDate: string) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    });
};

// Recently Viewed shows the time too, so two views on the same day can be told apart.
const formatDateTime = (isoDate?: string | null) => {
    if (!isoDate) {
        return '-';
    }
    const date = new Date(isoDate);
    // Local time, not UTC like the document date columns. This is a clock time the technician
    // can check against their own, so an offset reads as wrong rather than as a convention.
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const getFormattedDate = (isoDate: string) => {
    const dateObj = new Date(isoDate);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' };
    const formatDate = dateObj.toLocaleDateString('en-US', options);
    return formatDate;
};

const getDisplayName = ({ displayName, pubVersion }: { displayName: string; pubVersion: string }) => {
    return (parseVersion(pubVersion) ?? 0) > 1 ? `${displayName} ${`(${pubVersion})`}` : `${displayName}`;
};

const navigateUrl = ({ publicationId, status, version, isPreview }: NavigateUrl) => {
    const base = isPreview
        ? `/administration/publication/${publicationId}`
        : `/administration/publication/${publicationId}/edit`;
    const actionAndMaxVersion = `action=${StisPublicationAction.Edit}&status=${status}`;
    return `${base}?${actionAndMaxVersion}`;
};

export const getUrl = async (documentId: string, activityType: string, account: Account | null) => {
    try {
        const response = account
            ? await docsService.getDocumentUrl({
                  queryParameters: {
                      docId: documentId,
                      activityType: activityType,
                  },
                  ignoreCache: true,
              })
            : await docsService.getPublicDocumentUrl({
                  queryParameters: {
                      docId: documentId,
                      activityType: activityType,
                  },
                  ignoreCache: true,
              });
        if (response.success) {
            const url = response.data;
            return url;
        }
    } catch (error) {
        logger.error(`Failed to fetch the document URL [${documentId}]`, { error, documentId, activityType });
        return '';
    }
};

export const getServiceManualUrl = async (documentId: string) => {
    try {
        const response = await docsService.getServiceManualUrl({
            queryParameters: {
                docId: documentId,
            },
            ignoreCache: true,
        });
        if (response.success) {
            const url = response.data;
            return url;
        }
    } catch (error) {
        logger.error(`Failed to fetch the service manual URL [${documentId}]`, { error, documentId });
        return '';
    }
};

const StisTableBody = ({
    rowData,
    showRowsId,
    isManageAdminRoute,
    handleShowSubRows,
    renderIcons,
    onPopupBlocked,
    vin,
    totalCount,
    pageOffset,
    isSelectable,
    isSelected,
    onSelectChange,
    showLastViewed,
}: {
    rowData: RowData;
    showRowsId: string | undefined;
    isManageAdminRoute: PathMatch<'*'> | null;
    handleShowSubRows: (id: string) => void;
    renderIcons: (rowData: any) => JSX.Element;
    onPopupBlocked?: () => void;
    vin?: string;
    totalCount: number;
    pageOffset: number;
    isSelectable?: boolean;
    isSelected?: boolean;
    onSelectChange?: (docId: string) => void;
    showLastViewed?: boolean;
}) => {
    const classes = useStyles();
    const versionClasses = useStyleWrapper(showRowsId);
    const { account, token } = useAuthState();
    const { googleAnalyticsService } = useGoogleAnalytics();
    const checkShow = rowData.rowId === showRowsId;

    return (
        <>
            <TableRow
                key={rowData.rowId}
                style={{
                    backgroundColor:
                        account || (!account && rowData.visibilityLevel === 'FREE') ? '#FFFFFF' : '#F8F8F8',
                }}
                className={checkShow ? versionClasses.parentRow : ''}
            >
                {isSelectable && (
                    <TableCell>
                        <TableCheckbox checked={isSelected} onChange={() => onSelectChange?.(rowData.documentId)} />
                    </TableCell>
                )}

                <TableCell>
                    <div className={classes.documentType}>
                        <PublicationType publicationType={rowData.docType} size={20} />
                        <Typography numberOfLines={1} style={{ marginTop: 2 }}>
                            {rowData.docType}
                        </Typography>
                    </div>
                </TableCell>

                <TableCell>
                    {isManageAdminRoute == null ? (
                        <HoverModal
                            title={rowData.title || '-'}
                            description={rowData.description || '-'}
                            creationDate={rowData.createdDate ? getFormattedDate(rowData.createdDate) : '-'}
                            version={rowData.pubVersion || '-'}
                            toolNumber={rowData.toolNumber || '-'}
                            applicability={rowData.applicability || '-'}
                            index={rowData.index}
                            visibilityLevel={rowData.visibilityLevel || '-'}
                            disabled={rowData.docType === 'Software Update'}
                            onClick={async () => {
                                if (account || (!account && rowData.visibilityLevel === 'FREE')) {
                                    analyticsService.logEvent(ANALYTICS_EVENTS.STIS_RESULT_CLICKED, {
                                        screen: 'stis_publications',
                                        source: 'stis_table',
                                        documentId: rowData.documentId,
                                        type: rowData.docType,
                                        vin: vin,
                                        vinPresent: !!vin,
                                        resultCount: totalCount,
                                        resultPosition: pageOffset + rowData.index + 1,
                                    });

                                    let wasPopupBlocked = false;

                                    const documentOpened = await openDocumentTab(
                                        () => resolveDocumentUrl(rowData, account, token),
                                        () => {
                                            wasPopupBlocked = true;
                                            onPopupBlocked?.();
                                        }
                                    );

                                    if (documentOpened) {
                                        analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_OPENED, {
                                            screen: 'stis_publications',
                                            source: 'stis_table',
                                            documentId: rowData.documentId,
                                            type: rowData.docType,
                                            docType: rowData.docType,
                                            createdDate: rowData.createdDate ?? undefined,
                                            lastUpdated: rowData.lastUpdated ?? undefined,
                                            vin: vin,
                                            vinPresent: !!vin,
                                        });
                                        googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentOpen, {
                                            success: true,
                                            document_id: rowData.documentId,
                                            document_type: rowData.docType,
                                            entry_point: 'results_table',
                                        });
                                    } else {
                                        analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_OPEN_FAILED, {
                                            screen: 'stis_publications',
                                            source: 'stis_table',
                                            documentId: rowData.documentId,
                                            type: rowData.docType,
                                            errorMessage: 'Document tab was blocked or URL resolution failed',
                                            vin: vin,
                                            vinPresent: !!vin,
                                        });
                                        googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentOpen, {
                                            success: false,
                                            entry_point: 'results_table',
                                            document_id: rowData.documentId,
                                            document_type: rowData.docType,
                                            error_type: wasPopupBlocked ? 'popup_blocked' : 'open_failed',
                                        });
                                    }
                                }
                            }}
                        />
                    ) : (
                        <Link
                            to={navigateUrl({
                                publicationId: rowData.documentId,
                                status: rowData.status,
                                version: rowData.pubVersion,
                                isPreview: true,
                            })}
                        >
                            {getDisplayName({ displayName: rowData.title, pubVersion: rowData.pubVersion })}
                        </Link>
                    )}
                </TableCell>

                <TableCell>
                    <div>
                        <Typography numberOfLines={1}>{rowData.docCode}</Typography>
                    </div>
                </TableCell>

                {showLastViewed && (
                    <TableCell>
                        <div>
                            <Typography numberOfLines={1}>{formatDateTime(rowData.lastViewedAt)}</Typography>
                        </div>
                    </TableCell>
                )}

                <TableCell>
                    <div>
                        <Typography numberOfLines={1}>{formatDate(rowData.lastUpdated ?? '-')}</Typography>
                    </div>
                </TableCell>

                <TableCell>
                    <div>
                        <Typography numberOfLines={1}>{formatDate(rowData.createdDate ?? '-')}</Typography>
                    </div>
                </TableCell>

                {isManageAdminRoute !== null && (
                    <TableCell>
                        <div style={{ display: 'inline-block' }}>
                            <TextBadge variant={statusVariants[rowData.status]}>{rowData.status}</TextBadge>
                        </div>
                    </TableCell>
                )}

                <div className={classes.iconsContainer}>{renderIcons(rowData)}</div>
            </TableRow>
            {rowData.isExpanded &&
                rowData.associatedVersions &&
                rowData.associatedVersions.length > 0 &&
                rowData.associatedVersions.map((subRow: DocumentVersion, index: number) => {
                    return (
                        <TableRow
                            key={subRow.documentId}
                            className={`${versionClasses.subRows} ${
                                checkShow
                                    ? index === rowData.associatedVersions.length - 1
                                        ? versionClasses.bottomBorder
                                        : ''
                                    : 'd-none'
                            }`}
                        >
                            <TableCell>
                                <div className={classes.documentType}>
                                    <PublicationType publicationType={rowData.docType} size={20} />
                                    <Typography numberOfLines={1} style={{ marginTop: 2 }}>
                                        {subRow.docType}
                                    </Typography>
                                </div>
                            </TableCell>
                            <TableCell>
                                <Link
                                    to={navigateUrl({
                                        publicationId: subRow.documentId,
                                        status: subRow.status as unknown as PublicationStatusEnum,
                                        version: subRow.version,
                                        isPreview: true,
                                    })}
                                >
                                    {getDisplayName({ displayName: subRow.title, pubVersion: subRow.version })}
                                </Link>
                            </TableCell>
                            <TableCell>
                                <div>
                                    <Typography numberOfLines={1}>{subRow.docCode}</Typography>
                                </div>
                            </TableCell>

                            <TableCell>
                                <div>
                                    <Typography numberOfLines={1}>{formatDate(subRow.lastUpdated ?? '-')}</Typography>
                                </div>
                            </TableCell>

                            <TableCell>
                                <div>
                                    <Typography numberOfLines={1}>{formatDate(subRow.createdDate ?? '-')}</Typography>
                                </div>
                            </TableCell>

                            {isManageAdminRoute !== null && (
                                <TableCell>
                                    <div style={{ display: 'inline-block' }}>
                                        <TextBadge variant={statusVariants[subRow.status]}>{subRow.status}</TextBadge>
                                    </div>
                                </TableCell>
                            )}

                            <div className={classes.iconsContainer}>{renderIcons(subRow)}</div>
                        </TableRow>
                    );
                })}
        </>
    );
};

const StisTable: FC<StisTableProps> = (props) => {
    const {
        isLoading,
        publications,
        sortDirection,
        sortOrder,
        onSort,
        handleFavoriteClick,
        handleRemoveRecentClick,
        showLastViewed,
        handlePreviewButtonClick,
        getPublicationVersions,
        isManageAdminRoute,
        onPopupBlocked,
        totalElements,
        emptyStateDescription,
    } = props;
    const classes = useStyles();
    const { account, token } = useAuthState();
    const { googleAnalyticsService } = useGoogleAnalytics();
    const navigate = useNavigate();
    const { vin } = useParams<{ vin: string }>();
    const queryParams = useQuery();
    const textSearchQuery = queryParams.get('query') || '';
    const page = queryParams.get('page') || '0';
    const size = queryParams.get('size') || '10';
    const query = queryParams.get('query') || '';
    const pageOffset = parseInt(page, 10) * parseInt(size, 10);

    const [rowsData, setRowsData] = useState<RowData[]>([]);
    const [showRowsId, setShowRowsId] = useState<string>();
    const [show, setShow] = useState<boolean>(false);
    const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
    const publicationDispatch = usePublicationsDispatch();
    const isSelectable = account !== null && isManageAdminRoute === null && Boolean(handleRemoveRecentClick);
    const allSelected = publications.length > 0 && selectedDocIds.length === publications.length;

    // Drop selections for rows that are gone after a remove, a page change or a new tab.
    useEffect(() => {
        setSelectedDocIds((previous) =>
            previous.filter((docId) => publications.some((publication) => publication.documentId === docId))
        );
    }, [publications]);

    const handleSelectAllChange = useCallback(() => {
        setSelectedDocIds((previous) =>
            previous.length === publications.length ? [] : publications.map((publication) => publication.documentId)
        );
    }, [publications]);

    const handleRowSelectChange = useCallback((docId: string) => {
        setSelectedDocIds((previous) =>
            previous.includes(docId) ? previous.filter((id) => id !== docId) : [...previous, docId]
        );
    }, []);

    const handleRemoveSelected = useCallback(() => {
        if (!handleRemoveRecentClick || selectedDocIds.length === 0) {
            return;
        }
        handleRemoveRecentClick(selectedDocIds);
        analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_REMOVED_FROM_RECENTS, {
            screen: 'stis_publications',
            source: 'stis_table',
            documentCount: selectedDocIds.length,
            vin: vin,
            vinPresent: Boolean(vin),
        });
        setSelectedDocIds([]);
    }, [handleRemoveRecentClick, selectedDocIds, vin]);

    useEffect(() => {
        setRowsData(
            publications.map((row, index) => ({
                ...row,
                isExpanded: false,
                associatedVersions: [],
                rowId: index.toString(),
                index,
            }))
        );
    }, [publications]);

    useEffect(() => {
        handleShowSubRows('');
    }, [page, size, query]);

    const handleSort = useCallback(
        (sortBy: string, sortDirection: SORT_DIRECTION) => {
            onSort(sortBy, sortDirection);
        },
        [onSort]
    );

    const handleShowSubRows = useCallback(
        (id: string) => {
            setShow(!show);
            if (id === showRowsId) setShowRowsId('');
            else setShowRowsId(id);
        },
        [show, showRowsId]
    );

    const rawIcons = useMemo(
        () => (rowData: any) => [
            {
                // Preview isn't valid for Software Update (download-only) or Service Diagnostics
                // (multi-file HTML bundles), so it's disabled for both.
                icon:
                    account &&
                    textSearchQuery.length > 0 &&
                    !['Software Update', 'Service Diagnostics'].includes(rowData.docType) ? (
                        <Eye color="#1F518F" />
                    ) : (
                        <Eye style={{ opacity: 0.4 }} />
                    ),
                handleClickFunction: () => {
                    if (
                        account &&
                        textSearchQuery.length > 0 &&
                        !['Software Update', 'Service Diagnostics'].includes(rowData.docType)
                    ) {
                        handlePreviewButtonClick(
                            rowData.documentId,
                            rowData.title,
                            0,
                            rowData?.docType,
                            rowData?.fileName,
                            rowData?.createdDate,
                            rowData?.lastUpdated
                        );
                    }
                },
                show: isManageAdminRoute === null,
                tooltip: ['Software Update', 'Service Diagnostics'].includes(rowData.docType) ? '' : 'Preview',
                disableIcon: !(
                    account &&
                    textSearchQuery.length > 0 &&
                    !['Software Update', 'Service Diagnostics'].includes(rowData.docType)
                ),
            },
            {
                icon: <Share color={!account && rowData.visibilityLevel !== 'FREE' ? '#8FA3BC' : '#1F518F'} />,
                handleClickFunction:
                    account || (!account && rowData.visibilityLevel === 'FREE')
                        ? async () => {
                              // Share the bookmarkable CDN URL (authenticated users) so the copied
                              // link doesn't expire like the old S3 pre-signed one. Public/FREE users
                              // stay on the S3 link (they don't get CDN cookies).
                              const url = (await resolveDocumentUrl(rowData, account, token, 'share')) || '';
                              if (url) {
                                  handleCopy(
                                      url,
                                      publicationDispatch,
                                      publicationsActions.setToastMessage,
                                      'link copied to clipboard',
                                      'white',
                                      'checkmark'
                                  );
                                  analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_SHARED, {
                                      screen: 'stis_publications',
                                      source: 'stis_table',
                                      documentId: rowData.documentId,
                                      type: rowData.docType,
                                      docType: rowData.docType,
                                      vin: vin,
                                      vinPresent: !!vin,
                                  });
                              } else {
                                  publicationDispatch(
                                      publicationsActions.setToastMessage({
                                          toast: {
                                              toastMessage: 'Error in copying the link',
                                              iconColor: 'white',
                                              iconName: 'x-close',
                                          },
                                      })
                                  );
                              }
                          }
                        : () => {},
                show: isManageAdminRoute === null,
                // Software Update docs are download-only, so sharing a link is disabled too.
                tooltip: rowData.docType === 'Software Update' ? '' : 'Share',
                disableIcon: rowData.docType === 'Software Update',
            },
            {
                icon: <Download color={!account && rowData.visibilityLevel !== 'FREE' ? '#8FA3BC' : '#1F518F'} />,
                handleClickFunction:
                    account || (!account && rowData.visibilityLevel === 'FREE')
                        ? async (e: React.MouseEvent<HTMLDivElement>) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const options = {
                                  headers: {
                                      'X-Subcomm-Access-Token': token,
                                  },
                              };
                              let filename = '';
                              const isPrivate = !!account;
                              const url = isPrivate
                                  ? `${httpClient.vinUrl}/docs/download-doc?docId=${rowData.documentId}`
                                  : `${httpClient.vinUrl}/public/view-doc?docId=${rowData.documentId}`;
                              try {
                                  let fileBlob: Blob | undefined;
                                  if (isPrivate) {
                                      const res = await fetch(url, options);
                                      if (res.url) {
                                          let parsedUrl = new URL(res.url);
                                          const respContentDisp = Array.from(parsedUrl.searchParams).filter(
                                              (sp) => sp[0] === 'response-content-disposition'
                                          );
                                          if (respContentDisp && respContentDisp.length > 0) {
                                              const contentDispositionHeader = respContentDisp[0][1];
                                              if (contentDispositionHeader) {
                                                  let filenameSplited = contentDispositionHeader.split('filename=');
                                                  if (filenameSplited && filenameSplited.length > 1) {
                                                      filename = filenameSplited[1].split(';')[0].replaceAll('"', '');
                                                  }
                                              }
                                          }
                                      } else {
                                          let contentDispositionHeader = res.headers.get('Content-Disposition');
                                          if (contentDispositionHeader) {
                                              let filenameSplited = contentDispositionHeader.split('filename=');
                                              if (filenameSplited && filenameSplited.length > 1) {
                                                  filename = filenameSplited[1].split(';')[0].replaceAll('"', '');
                                              }
                                          }
                                      }
                                      fileBlob = await res.blob();
                                  } else {
                                      const res = await fetch(url);
                                      const s3Url = await res.text();
                                      const parsedUrl = new URL(s3Url);
                                      const contentDispositionParam =
                                          parsedUrl.searchParams.get('response-content-disposition');
                                      if (contentDispositionParam?.includes('filename=')) {
                                          const filenameSplited = contentDispositionParam.split('filename=');
                                          if (filenameSplited.length > 1) {
                                              filename = filenameSplited[1].replaceAll('"', '');
                                          }
                                      }
                                      const s3Res = await fetch(s3Url);
                                      if (!s3Res.ok) {
                                          throw new Error('Failed to fetch file from S3');
                                      }
                                      fileBlob = await s3Res.blob();
                                  }
                                  if (fileBlob) {
                                      const file = window.URL.createObjectURL(fileBlob);
                                      if (!filename) {
                                          filename = `document_${rowData.documentId || 'temp'}.pdf`;
                                      }

                                      analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_DOWNLOADED, {
                                          screen: 'stis_publications',
                                          source: 'stis_table',
                                          documentId: rowData.documentId,
                                          type: rowData.docType,
                                          docType: rowData.docType,
                                          createdDate: rowData.createdDate,
                                          vin: vin,
                                          vinPresent: !!vin,
                                          lastUpdated: rowData.lastUpdated,
                                      });

                                      googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentDownload, {
                                          success: true,
                                          entry_point: 'results_table',
                                          document_id: rowData.documentId,
                                          document_type: rowData.docType,
                                      });

                                      if (filename) {
                                          let a = document.createElement('a');
                                          document.body.appendChild(a);
                                          a.setAttribute('style', 'display: none');
                                          a.href = file;
                                          a.download = filename;
                                          a.click();
                                          window.URL.revokeObjectURL(file);
                                          a.remove();
                                      } else {
                                          window.location.assign(file);
                                      }
                                  } else {
                                      googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentDownload, {
                                          success: false,
                                          entry_point: 'results_table',
                                          document_id: rowData?.documentId,
                                          document_type: rowData?.docType,
                                          error_type: 'file_blob_not_found',
                                      });
                                      publicationDispatch(
                                          publicationsActions.setToastMessage({
                                              toast: {
                                                  toastMessage: 'Error: Unable to process the file',
                                                  iconColor: 'white',
                                                  iconName: 'x-close',
                                              },
                                          })
                                      );
                                  }
                              } catch (error) {
                                  googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentDownload, {
                                      success: false,
                                      entry_point: 'results_table',
                                      document_id: rowData?.documentId,
                                      document_type: rowData?.docType,
                                      error_type: 'unknown_error',
                                  });
                                  publicationDispatch(
                                      publicationsActions.setToastMessage({
                                          toast: {
                                              toastMessage: 'Error: Unable to process the file',
                                              iconColor: 'white',
                                              iconName: 'x-close',
                                          },
                                      })
                                  );
                              }
                          }
                        : () => {},
                show: isManageAdminRoute === null,
                tooltip: rowData.docType === 'Service Diagnostics' ? '' : 'Download',
                // Service Diagnostics are multi-file HTML bundles, not a single downloadable file,
                // so download is disabled (greyed out) for that document type.
                disableIcon: rowData.docType === 'Service Diagnostics',
            },
            {
                icon: rowData.isFavorite ? <Favourite /> : <FavouriteOpen />,
                handleClickFunction: () => {
                    handleFavoriteClick(rowData.documentId, !rowData.isFavorite);
                    analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_FAVORITED, {
                        screen: 'stis_publications',
                        source: 'stis_table',
                        documentId: rowData.documentId,
                        type: rowData.docType,
                        vin: vin,
                        vinPresent: !!vin,
                        isFavorite: !rowData.isFavorite,
                    });
                },
                show: account !== null && isManageAdminRoute === null,
                tooltip: rowData.isFavorite ? 'Remove Favorite' : 'Favorite',
            },
            {
                icon: <Icon name="pencil" color="blueOne" />,
                handleClickFunction: () =>
                    navigate(
                        navigateUrl({
                            publicationId: rowData.documentId,
                            status: rowData.status,
                            version: rowData.pubVersion,
                            isPreview: true,
                        })
                    ),
                show: isManageAdminRoute !== null,
                tooltip: 'Edit',
            },
            {
                icon: <Icon name="library-add" color="blueOne" />,
                handleClickFunction: async () => {
                    const nextVersion = getNextVersion(rowData.pubVersion);
                    const response = await docsService.createVersionDocumentId({
                        pubVersion: nextVersion,
                        parentDocumentId: rowData.documentId,
                    });
                    if (response && !response.success) {
                        publicationDispatch(
                            publicationsActions.setToastMessage({
                                toast: {
                                    toastMessage: getSaveErrorMessage(
                                        response.data,
                                        'The publication version was not created'
                                    ),
                                    iconColor: 'white',
                                    iconName: 'x-close',
                                },
                            })
                        );
                    } else {
                        if (typeof response.data === 'string') {
                            try {
                                const parsedData = JSON.parse(response.data);
                                if (parsedData && parsedData.documentId) {
                                    navigate(
                                        `/administration/publication/${parsedData.documentId}?action=${
                                            StisPublicationAction.AddVersion
                                        }&maxVersion=${nextVersion}&status=${PublicationStatusEnum.Draft}`
                                    );
                                }
                            } catch (error) {
                                logger.error(
                                    `Failed to parse the create version response data for the publication [${rowData.documentId}]`,
                                    { error, documentId: rowData.documentId }
                                );
                            }
                        }
                    }
                },
                show: isManageAdminRoute !== null && rowData.status !== 'DRAFT',
                tooltip: rowData.disablePublished ? '' : 'Add Version',
                disableIcon: rowData.disablePublished ? true : false,
            },
            {
                icon: <Icon name={`chevron-${rowData.isExpanded ? 'up' : 'down'}`} color="blueOne" />,
                handleClickFunction: async () => {
                    if (!rowData.isExpanded) {
                        const associatedVersions = (await getPublicationVersions(
                            rowData.documentId
                        )) as unknown as Array<DocumentVersion>;
                        if (associatedVersions) {
                            rowData.associatedVersions = associatedVersions
                                .filter((_, index) => index !== 0)
                                .map((version) => ({ ...version, disablePublished: true }));
                        }
                    }
                    rowData.isExpanded = !rowData.isExpanded;
                    setRowsData((prevRows: any[]) =>
                        prevRows.map((row) =>
                            row.documentId === rowData.documentId ? { ...row, isExpanded: rowData.isExpanded } : row
                        )
                    );
                    handleShowSubRows(rowData.rowId);
                },
                show: isManageAdminRoute !== null && Boolean(rowData.hasVersions),
                tooltip: rowData.isExpanded ? 'Collapse' : 'Expand',
            },
        ],
        [
            rowsData,
            publications,
            account,
            isManageAdminRoute,
            handleFavoriteClick,
            getPublicationVersions,
            showRowsId,
            show,
        ]
    );

    const renderIcons = (rowData: any) => (
        <div className={classes.iconsContainer}>
            {rawIcons(rowData).map(
                (iconObj, index) =>
                    iconObj.show && (
                        <OverlayTrigger
                            key={index}
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            trigger={
                                account || (!account && rowData.visibilityLevel === 'FREE') ? ['hover', 'focus'] : []
                            }
                            overlay={
                                <Tooltip
                                    show={iconObj.tooltip.length > 0}
                                    id={`tooltip-${index}`}
                                    className={classes.customTooltip}
                                >
                                    {iconObj.tooltip}
                                </Tooltip>
                            }
                        >
                            <div
                                key={index}
                                className={classes.icons}
                                onClick={iconObj.disableIcon ? undefined : iconObj.handleClickFunction}
                                style={{
                                    cursor: iconObj.disableIcon
                                        ? 'not-allowed'
                                        : account || (!account && rowData.visibilityLevel === 'FREE')
                                          ? 'pointer'
                                          : 'not-allowed',
                                    opacity: iconObj.disableIcon ? 0.5 : 1,
                                }}
                            >
                                {iconObj.icon}
                            </div>
                        </OverlayTrigger>
                    )
            )}
        </div>
    );

    return (
        <div style={{ padding: 20 }}>
            <TableRenderer<any>
                isLoading={isLoading}
                tableRowsData={rowsData}
                tableRowsDataSetter={setRowsData}
                noResultsTitle={'No results found'}
                outerTableClassName={classes.outerTableClass}
                emptyStateProps={{
                    image: <NoFoundError />,
                    description: emptyStateDescription,
                    descriptionStyle: { fontWeight: '400', fontSize: 12, lineHeight: 15 },
                }}
                tableHeaderRowRenderer={() => {
                    return (
                        <TableRow>
                            {isSelectable && (
                                <TableHeader fixed hideBorder={selectedDocIds.length > 0}>
                                    <TableCheckbox
                                        checked={allSelected}
                                        onChange={handleSelectAllChange}
                                        iconName={allSelected ? 'checkmark' : 'minus'}
                                    />
                                </TableHeader>
                            )}
                            {isSelectable && selectedDocIds.length > 0 ? (
                                <TableActionHeader
                                    colspan={showLastViewed ? 7 : 6}
                                    selectedCount={selectedDocIds.length}
                                >
                                    <Button
                                        variant="ghost-blue"
                                        iconLeft={<Icon name="trash" color="blueOne" size={20} />}
                                        onPress={handleRemoveSelected}
                                    >
                                        Remove from Recently Viewed
                                    </Button>
                                </TableActionHeader>
                            ) : (
                                <>
                                    <TableHeader
                                        sortable
                                        sortDirection={sortOrder === 'documentType' ? sortDirection : undefined}
                                        onSort={(_event, sortDirection) => {
                                            handleSort('documentType', sortDirection);
                                        }}
                                    >
                                        <Typography variant="label" numberOfLines={1}>
                                            Document Type
                                        </Typography>
                                    </TableHeader>
                                    <TableHeader
                                        sortable
                                        sortDirection={sortOrder === 'title' ? sortDirection : undefined}
                                        onSort={(_event, sortDirection) => {
                                            handleSort('title', sortDirection);
                                        }}
                                    >
                                        <Typography variant="label" numberOfLines={1}>
                                            Title / ID / Case No
                                        </Typography>
                                    </TableHeader>
                                    <TableHeader
                                        sortable
                                        sortDirection={sortOrder === 'documentCode' ? sortDirection : undefined}
                                        onSort={(_event, sortDirection) => {
                                            handleSort('documentCode', sortDirection);
                                        }}
                                    >
                                        <Typography variant="label" numberOfLines={1}>
                                            Doc Code
                                        </Typography>
                                    </TableHeader>
                                    {showLastViewed && (
                                        <TableHeader>
                                            <Typography variant="label" numberOfLines={1}>
                                                Last Viewed
                                            </Typography>
                                        </TableHeader>
                                    )}
                                    <TableHeader
                                        sortable
                                        sortDirection={sortOrder === 'updatedAt' ? sortDirection : undefined}
                                        onSort={(_event, sortDirection) => {
                                            handleSort('updatedAt', sortDirection);
                                        }}
                                    >
                                        <Typography variant="label" numberOfLines={1}>
                                            Updated Date
                                        </Typography>
                                    </TableHeader>
                                    <TableHeader
                                        sortable
                                        sortDirection={sortOrder === 'createdAt' ? sortDirection : undefined}
                                        onSort={(_event, sortDirection) => {
                                            handleSort('createdAt', sortDirection);
                                        }}
                                    >
                                        <Typography variant="label" numberOfLines={1}>
                                            Created Date
                                        </Typography>
                                    </TableHeader>
                                    {isManageAdminRoute !== null && (
                                        <TableHeader>
                                            <Typography variant="label" numberOfLines={1}>
                                                Status
                                            </Typography>
                                        </TableHeader>
                                    )}
                                    <TableHeader>
                                        <Typography variant="label" numberOfLines={1}>
                                            Actions
                                        </Typography>
                                    </TableHeader>
                                </>
                            )}
                        </TableRow>
                    );
                }}
                tableBodyRowRenderer={(rowData) => {
                    return (
                        <StisTableBody
                            rowData={rowData}
                            showRowsId={showRowsId}
                            isManageAdminRoute={isManageAdminRoute ?? null}
                            handleShowSubRows={handleShowSubRows}
                            renderIcons={renderIcons}
                            onPopupBlocked={onPopupBlocked}
                            vin={vin}
                            totalCount={totalElements ?? publications.length}
                            pageOffset={pageOffset}
                            isSelectable={isSelectable}
                            isSelected={selectedDocIds.includes(rowData.documentId)}
                            onSelectChange={handleRowSelectChange}
                            showLastViewed={showLastViewed}
                        />
                    );
                }}
                isPublicUser={account ? false : true}
            />
        </div>
    );
};

export default StisTable;
